import {
  createSlice
} from '@reduxjs/toolkit';
import type {
  PayloadAction
} from '@reduxjs/toolkit';

import {
  TimePeriodSelectOption
} from 'src/shared/components';

import {
  CommunityTabId,
  TabState,
  ActiveTabSortBy,
  FollowingTabSortBy,
  ScoredTabSortBy,
  EmptyMessage,
  SortByInCommunity,
  ScoredSelectSetsOption,
  ScoredTabFilters,
} from './types';

export interface CommunityState {
  [CommunityTabId.Active]: TabState<ActiveTabSortBy>;
  [CommunityTabId.Followings]: TabState<FollowingTabSortBy>;
  [CommunityTabId.Scored]: TabState<ScoredTabSortBy>;
  lastActiveTab: CommunityTabId | null;
  scoredTabFilters: ScoredTabFilters | null;
}

export interface CommunityReducer {
  community: CommunityState;
}

const getDefaultState = <T>(sortBy: T) => ({
  loading: false,
  emptyMessage: {
    title: '',
    subtitle: '',
  },
  sortBy,
});

export const initialState: CommunityState = {
  [CommunityTabId.Active]: getDefaultState(ActiveTabSortBy.Latest),
  [CommunityTabId.Followings]: getDefaultState(FollowingTabSortBy.MostEngaged),
  [CommunityTabId.Scored]: getDefaultState(ScoredTabSortBy.Newest),
  lastActiveTab: null,
  scoredTabFilters: null,
};

export const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    setCommunityTabLoading: (
      state,
      action: PayloadAction<{ tabName: CommunityTabId; loading: boolean }>,
    ) => {
      state[action.payload.tabName].loading = action.payload.loading;
    },
    setCommunityTabEmptyMessage: (
      state,
      action: PayloadAction<{
        tabName: CommunityTabId;
        emptyMessage: EmptyMessage;
      }>,
    ) => {
      state[action.payload.tabName].emptyMessage = action.payload.emptyMessage;
    },
    setCommunitySortBy: <T extends SortByInCommunity>(
      state: CommunityState,
      action: PayloadAction<{ tabName: CommunityTabId; sortBy: T }>,
    ) => {
      state[action.payload.tabName].sortBy = action.payload.sortBy;
    },
    setCommunityScoredTabSelectedSets: (
      state: CommunityState,
      action: PayloadAction<{ selectedSets: ScoredSelectSetsOption[] }>,
    ) => {
      const {
        selectedSets
      } = action.payload;

      if (!state.scoredTabFilters) {
        state.scoredTabFilters = {
          selectedSets,
          timePeriod: null,
        };
      } else {
        state.scoredTabFilters.selectedSets = selectedSets;
      }
    },
    setCommunityScoredTabTimePeriod: (
      state: CommunityState,
      action: PayloadAction<{ timePeriod: TimePeriodSelectOption | null }>,
    ) => {
      const {
        timePeriod
      } = action.payload;

      if (!state.scoredTabFilters) {
        state.scoredTabFilters = {
          selectedSets: [],
          timePeriod,
        };
      } else {
        state.scoredTabFilters.timePeriod = timePeriod;
      }
    },
    setCommunityLastActiveTab: (
      state: CommunityState,
      action: PayloadAction<{ tab: CommunityTabId }>,
    ) => {
      state.lastActiveTab = action.payload.tab;
    },
  },
});

export const {
  setCommunityTabLoading,
  setCommunityTabEmptyMessage,
  setCommunitySortBy,
  setCommunityScoredTabSelectedSets,
  setCommunityScoredTabTimePeriod,
  setCommunityLastActiveTab,
} = communitySlice.actions;

export const communityReducer = communitySlice.reducer;
